import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { THEMES, useTheme } from '@/lib/theme'

export function ThemePicker() {
  const { theme, setTheme } = useTheme()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', close)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', close)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Theme"
        aria-expanded={open}
        className="btn-secondary h-full px-2.5 py-2 text-sm sm:px-3"
      >
        <svg aria-hidden viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" className="size-5 sm:size-4">
          <path d="M10 2.5a7.5 7.5 0 1 0 0 15c1 0 1.5-.7 1.5-1.4 0-.9-.8-1.3-.8-2.1 0-.8.7-1.5 1.5-1.5h1.6a3.7 3.7 0 0 0 3.7-3.7C17.5 5.2 14.2 2.5 10 2.5Z" />
          <circle cx="6.5" cy="9" r="1" fill="currentColor" />
          <circle cx="9" cy="6" r="1" fill="currentColor" />
          <circle cx="12.5" cy="6.5" r="1" fill="currentColor" />
        </svg>
      </button>
      {open && (
        <div role="radiogroup" aria-label="Theme" className="panel absolute right-0 z-20 mt-2 w-60 space-y-1 p-2">
          {THEMES.map((t) => (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={theme === t.id}
              onClick={() => setTheme(t.id)}
              className={clsx('menu-item flex w-full items-center gap-3 px-3 py-2 text-left', theme === t.id && 'font-semibold')}
            >
              <span aria-hidden className="flex shrink-0 -space-x-1.5">
                {t.swatch.map((color) => (
                  <span key={color} className="size-5 rounded-full ring-2 ring-white dark:ring-slate-900" style={{ background: color }} />
                ))}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block">{t.name}</span>
                <span className="muted block text-xs font-normal">{t.description}</span>
              </span>
              {theme === t.id && <span aria-hidden>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
